/**
 * DeleteSceneSheet — FE-210.
 *
 * Confirmation modal for "delete a scene" from Browse > Scenes.
 * Lets the user pick between:
 *
 *   - Keep the items (cascade=false, default). FileRecord is
 *     dropped; linked inventory rows orphan with `source_file_id`
 *     := null and survive in the user's inventory.
 *
 *   - Delete the items too (cascade=true). Aggressive — FileRecord
 *     AND every linked inventory row vanishes.
 *
 * Wire-up:
 *   - Parent passes `visible`, `scene` (the FileWithInventoryResponse
 *     row we're operating on), and `onClose`.
 *   - Submit calls `deleteScene` thunk; on fulfilled, toast + close.
 *   - On error, surface inline + keep the modal open so the user can
 *     retry without losing their cascade pick.
 */

import React, { useState } from 'react';
import {
  Modal,
  Pressable,
  StyleSheet,
  Text,
  View,
  ActivityIndicator,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import Toast from 'react-native-toast-message';
import { fpColors } from '../../../styles/theme';
import { useAppDispatch } from '../../../store/store';
import { deleteScene } from '../browseSlice';
import type { FileWithInventoryResponse } from '../../../api/types';

interface DeleteSceneSheetProps {
  visible: boolean;
  scene: FileWithInventoryResponse | null;
  onClose: () => void;
  /** Optional callback fired after a successful delete, after the
   *  toast has been queued. Used by parent to e.g. reset scrolling
   *  position or trigger a refetch. */
  onDeleted?: (info: { fileId: number; cascade: boolean }) => void;
}

export const DeleteSceneSheet: React.FC<DeleteSceneSheetProps> = ({
  visible,
  scene,
  onClose,
  onDeleted,
}) => {
  const dispatch = useAppDispatch();
  const [cascade, setCascade] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset every open so an aborted delete doesn't pre-tick cascade
  // for the next scene.
  React.useEffect(() => {
    if (!visible) return;
    setCascade(false);
    setSubmitting(false);
    setError(null);
  }, [visible]);

  if (!scene) return null;

  const itemCount = scene.inventory_items?.length ?? 0;
  const sceneLabel =
    (scene as any).scene_label ||
    scene.filename ||
    `Snapshot #${scene.id}`;

  const handleConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await dispatch(
        deleteScene({ fileId: scene.id, cascade }),
      ).unwrap();
      Toast.show({
        type: 'success',
        text1: cascade
          ? `Snapshot + ${itemCount} item${itemCount === 1 ? '' : 's'} deleted`
          : itemCount > 0
            ? `Snapshot deleted · ${itemCount} item${itemCount === 1 ? '' : 's'} kept`
            : 'Snapshot deleted',
      });
      onDeleted?.({ fileId: scene.id, cascade });
      onClose();
    } catch (err: any) {
      setError(
        typeof err === 'string'
          ? err
          : err?.message ?? 'Couldn’t delete the snapshot. Try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={styles.sheet}>
          <View style={styles.headerRow}>
            <Text style={styles.title}>Delete this snapshot?</Text>
            <Pressable
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel="Close"
              hitSlop={8}
            >
              <MaterialIcons name="close" size={22} color={fpColors.onSurface} />
            </Pressable>
          </View>

          <Text style={styles.sceneLabel} numberOfLines={1}>
            {sceneLabel}
          </Text>
          <Text style={styles.subtitle}>
            {itemCount === 0
              ? 'This snapshot has no items.'
              : `${itemCount} item${itemCount === 1 ? '' : 's'} belong to this snapshot.`}
          </Text>

          {itemCount > 0 ? (
            <>
              <Text style={styles.sectionLabel}>
                Choose what happens to the items:
              </Text>

              <Pressable
                onPress={() => setCascade(false)}
                accessibilityRole="radio"
                accessibilityState={{ selected: !cascade }}
                style={[
                  styles.option,
                  !cascade && styles.optionActive,
                ]}
                testID="delete-scene-option-keep"
              >
                <View style={[styles.radio, !cascade && styles.radioOn]} />
                <View style={{ flex: 1 }}>
                  <Text style={styles.optionTitle}>Keep the items</Text>
                  <Text style={styles.optionBody}>
                    The items stay in your inventory. Just the snapshot grouping is removed.
                  </Text>
                </View>
              </Pressable>

              <Pressable
                onPress={() => setCascade(true)}
                accessibilityRole="radio"
                accessibilityState={{ selected: cascade }}
                style={[
                  styles.option,
                  cascade && styles.optionActiveDanger,
                ]}
                testID="delete-scene-option-cascade"
              >
                <View
                  style={[
                    styles.radio,
                    cascade && styles.radioOnDanger,
                  ]}
                />
                <View style={{ flex: 1 }}>
                  <Text style={styles.optionTitle}>Delete the items too</Text>
                  <Text style={styles.optionBody}>
                    All {itemCount} item{itemCount === 1 ? '' : 's'} {itemCount === 1 ? 'is' : 'are'} permanently deleted from your inventory.
                  </Text>
                </View>
              </Pressable>
            </>
          ) : null}

          {error ? (
            <View style={styles.errorBanner}>
              <MaterialIcons
                name="error-outline"
                size={16}
                color={fpColors.error}
              />
              <Text style={styles.errorText} numberOfLines={2}>
                {error}
              </Text>
            </View>
          ) : null}

          <View style={styles.actions}>
            <Pressable
              onPress={onClose}
              style={({ pressed }) => [
                styles.cancelBtn,
                pressed && { opacity: 0.85 },
              ]}
              accessibilityRole="button"
              accessibilityLabel="Cancel"
            >
              <Text style={styles.cancelBtnText}>Cancel</Text>
            </Pressable>
            <View style={styles.confirmBtnWrap}>
              {submitting ? (
                <View style={styles.spinner} testID="delete-scene-spinner">
                  <ActivityIndicator color={fpColors.onPrimary} />
                </View>
              ) : (
                // Inline confirm button — PrimaryCTA doesn't accept
                // testID and we want a destructive accent that's
                // distinct from the brand-purple primary anyway.
                <Pressable
                  onPress={handleConfirm}
                  testID="delete-scene-confirm"
                  accessibilityRole="button"
                  accessibilityLabel={
                    cascade ? 'Delete snapshot and items' : 'Delete snapshot'
                  }
                  style={({ pressed }) => [
                    styles.confirmBtn,
                    cascade && styles.confirmBtnDanger,
                    pressed && { opacity: 0.9 },
                  ]}
                >
                  <Text style={styles.confirmBtnText}>
                    {cascade ? 'Delete snapshot + items' : 'Delete snapshot'}
                  </Text>
                </Pressable>
              )}
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  sheet: {
    width: '100%',
    maxWidth: 480,
    backgroundColor: fpColors.surface,
    borderRadius: 16,
    padding: 20,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  title: {
    fontFamily: 'Inter_700Bold',
    fontSize: 18,
    color: fpColors.onSurface,
  },
  sceneLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: fpColors.onSurface,
    marginBottom: 2,
  },
  subtitle: {
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
    marginBottom: 16,
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: fpColors.onSurfaceVariant,
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    backgroundColor: fpColors.surfaceContainerLowest,
    marginBottom: 8,
  },
  optionActive: {
    borderColor: fpColors.primary,
    backgroundColor: fpColors.primaryContainer,
  },
  optionActiveDanger: {
    borderColor: fpColors.error,
    backgroundColor: 'rgba(186,26,26,0.08)',
  },
  radio: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 2,
    borderColor: fpColors.outline,
    marginTop: 2,
  },
  radioOn: {
    borderColor: fpColors.primary,
    backgroundColor: fpColors.primary,
  },
  radioOnDanger: {
    borderColor: fpColors.error,
    backgroundColor: fpColors.error,
  },
  optionTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: fpColors.onSurface,
    marginBottom: 2,
  },
  optionBody: {
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    lineHeight: 16,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 10,
    backgroundColor: 'rgba(186,26,26,0.08)',
    borderRadius: 8,
    marginTop: 8,
    marginBottom: 4,
  },
  errorText: {
    flex: 1,
    fontSize: 12,
    color: fpColors.error,
  },
  actions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 16,
  },
  cancelBtn: {
    flex: 1,
    padding: 12,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    backgroundColor: fpColors.surfaceContainerLowest,
  },
  cancelBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: fpColors.onSurfaceVariant,
  },
  confirmBtnWrap: {
    flex: 2,
  },
  spinner: {
    height: 44,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: fpColors.primary,
  },
  confirmBtn: {
    height: 44,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: fpColors.primary,
  },
  confirmBtnDanger: {
    backgroundColor: fpColors.error,
  },
  confirmBtnText: {
    fontSize: 14,
    fontWeight: '700',
    color: fpColors.onPrimary,
  },
});
